<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{$t('system.text_479')}}</div>
    <div slot="body">
      <dialog-selected-tips :name="$t('dictionary.user')" :count="params.data.length" :action="params.title" />
      <dialog-table :data="params.data" :columns="params.columns.slice(0, 3)" />
      <a-form
        :form="form.fc"
        v-bind="formItemLayout">
        <a-form-item :label="$t('scope.text_245')" v-if="params.data.length<=1">
          <a-input :placeholder="$t('system.displayname_tip')" v-decorator="decorators.displayname" />
        </a-form-item>
        <a-form-item :label="$t('scope.text_791')">
          <a-date-picker
            v-decorator="decorators.expired_at"
            :show-time="{ format: 'HH:mm' }"
            format="YYYY-MM-DD HH:mm"
            :placeholder="$t('scope.text_795')"
            style="width: 100%" />
        </a-form-item>
        <a-form-item :label="$t('system.text_475')">
          <a-switch :checkedChildren="$t('system.text_134')" :unCheckedChildren="$t('system.text_135')" v-decorator="decorators['allow_web_console']" />
        </a-form-item>
        <a-form-item :label="$t('system.text_483')" :extra="mfaExtra">
          <a-switch :disabled="!!mfaExtra" :checkedChildren="$t('system.text_134')" :unCheckedChildren="$t('system.text_135')" v-decorator="decorators['enable_mfa']" />
        </a-form-item>
      </a-form>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ submitButtonText }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import workflowMixin from '@/mixins/workflow'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import { uuid } from '@/utils/utils'

export default {
  name: 'UserUpdateDialog',
  mixins: [DialogMixin, WindowsMixin, workflowMixin],
  data () {
    return {
      loading: false,
      form: {
        fc: this.$form.createForm(this),
      },
      decorators: {
        allow_web_console: [
          'allow_web_console',
          {
            initialValue: this.params.data[0].allow_web_console,
            valuePropName: 'checked',
          },
        ],
        enable_mfa: [
          'enable_mfa',
          {
            initialValue: this.params.data[0].enable_mfa,
            valuePropName: 'checked',
          },
        ],
        displayname: [
          'displayname',
          {
            initialValue: this.params.data[0].displayname || '',
            rules: [{ required: false }],
          },
        ],
        expired_at: [
          'expired_at',
          {
            initialValue: this.params.data[0].expired_at ? moment(this.params.data[0].expired_at) : null,
          },
        ],
      },
      formItemLayout: {
        wrapperCol: {
          span: 19,
        },
        labelCol: {
          span: 5,
        },
      },
      mfaExtra: '',
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    isExecuteResourceOrderSetEnabled () {
      return this.checkWorkflowEnabled(WORKFLOW_TYPES.EXECUTE_USER_UPDATE)
    },
    submitButtonText () {
      return this.isExecuteResourceOrderSetEnabled ? this.$t('system.text_532') : this.$t('dialog.ok')
    },
  },
  created () {
    this.fetchApiServiceConfig()
  },
  methods: {
    async fetchApiServiceConfig () {
      try {
        const response = await new this.$Manager('service_settings', 'v1').list()
        const config = (response.data && response.data.yunionapi) || {}
        if (!config.enable_totp) {
          this.mfaExtra = this.$t('system.text_509')
        }
        return config
      } catch (error) {
        if (error.response && error.response.status) {
          this.mfaExtra = this.$t('system.text_510')
        }
        throw error
      }
    },
    async handleConfirm () {
      this.loading = true
      const { data = [], onManager } = this.params
      try {
        const values = await this.form.fc.validateFields()
        // 格式化 expired_at 为 ISO 字符串
        if (values.expired_at) {
          values.expired_at = moment(values.expired_at).toISOString()
        } else {
          values.clear_expire = true
        }
        // 如果启用了 execute-user-update 工单类型，则创建工单
        if (this.isExecuteResourceOrderSetEnabled) {
          await this.updateUserByWorkflow(data, values)
        } else {
          // 否则直接更新
          let isSelf = false
          await onManager('batchUpdate', {
            id: data.map(({ id }) => {
              if (id === this.userInfo.id) isSelf = true
              return id
            }),
            managerArgs: {
              data: values,
            },
          })
          this.cancelDialog()
          // 当是本人时，更新用户信息
          if (data.length === 1 && isSelf) {
            await this.$store.dispatch('auth/getInfo')
          }
        }
      } catch (error) {
        this.loading = false
        throw error
      }
    },
    async updateUserByWorkflow (data, values) {
      try {
        // 获取第一个用户的信息（用于确定项目和项目域）
        const userData = data[0]
        // 获取用户的项目信息（如果有）
        const projectId = userData.project_id || this.userInfo.projectId
        const domainId = userData.domain_id || this.userInfo.projectDomainId || 'default'

        // 创建 resource_order_sets
        const orderSetManager = new this.$Manager('resource_order_sets')
        const { displayname, name } = this.userInfo
        const projectDomainName = userData.domain || (domainId === 'default' ? 'Default' : domainId)

        // 为每个用户创建一个参数
        const parameters = data.map(user => ({
          action: 'update',
          resource: 'users',
          resource_id: user.id,
          project: projectId || '',
          project_domain: projectDomainName,
          parameter: values,
        }))

        const orderSetData = {
          auto_execute: false,
          name: this.$t('iam.user_update_workflow_name', [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
          parameters,
        }
        const orderSetRes = await orderSetManager.create({ data: orderSetData })

        // 创建工单
        const variables = {
          process_definition_key: WORKFLOW_TYPES.EXECUTE_USER_UPDATE,
          initiator: this.userInfo.id,
          ids: orderSetRes.data.id,
          parameter: '{}',
          project: projectId || '',
          project_domain: domainId,
          execute_resource_order_set_type: 'users_update',
        }
        await this.createWorkflow(variables)
        this.$message.success(this.$t('common.worflow_tip', [this.$t('system.text_479')]))
        this.cancelDialog()
        this.$router.push('/workflow')
      } catch (error) {
        throw error
      }
    },
  },
}
</script>
